(() => {
  if (customElements.get('contact-attachments')) return;

  const MAX_FILES = 20;
  const IMAGE_LIMIT = 10 * 1024 * 1024;
  const VIDEO_LIMIT = 100 * 1024 * 1024;
  const CHUNK_SIZE = 5 * 1024 * 1024;
  const formatSize = (size) => `${(size / (1024 * 1024)).toFixed(1)}MB`;
  const fileKind = (file) => {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('video/')) return 'video';
    if (file.type && file.type !== 'application/octet-stream') return '';
    if (/\.(avif|bmp|gif|heic|heif|jpe?g|png|svg|tiff?|webp)$/i.test(file.name)) return 'image';
    if (/\.(avi|m4v|mkv|mov|mp4|mpe?g|ogv|webm|wmv)$/i.test(file.name)) return 'video';
    return '';
  };

  class ContactAttachments extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.form = this.closest('form');
      this.input = this.querySelector('[data-attachment-input]');
      this.trigger = this.querySelector('[data-attachment-trigger]');
      this.list = this.querySelector('[data-attachment-files]');
      this.status = this.querySelector('[data-attachment-status]');
      this.links = this.querySelector('[data-attachment-links]');
      this.submit = this.form?.querySelector('[type="submit"]');
      if (!this.form || !this.input || !this.trigger || !this.links || !this.submit) return;

      this.cloud = this.dataset.cloudName.trim();
      this.preset = this.dataset.uploadPreset.trim();
      this.storageKey = `fullot-contact-files:${location.pathname}:${this.dataset.sectionId}`;
      this.items = [];
      this.busy = false;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.trigger.disabled = !this.cloud || !this.preset;
      if (this.trigger.disabled) this.setStatus('Attachments are temporarily unavailable. Please try again later.', true);

      this.restore();
      this.render();
      this.trigger.addEventListener('click', () => this.input.click(), options);
      this.input.addEventListener('change', () => this.selectFiles(), options);
      this.form.addEventListener('submit', (event) => {
        this.sync();
        if (this.items.some((item) => item.state !== 'done')) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.setStatus(this.busy
            ? 'Please wait until all files finish uploading.'
            : 'Please retry or remove the files that could not be uploaded before sending.', true);
          this.status.focus();
        }
        // Successful uploads use the original Shopify submission, including CAPTCHA.
        // Do not call form.submit(): it bypasses validation and submit listeners.
      }, { ...options, capture: true });
      this.form.addEventListener('reset', () => {
        this.items.forEach((item) => this.release(item));
        this.items = [];
        this.sync();
        this.render();
        this.setStatus('');
      }, options);
    }

    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
      this.items?.forEach((item) => this.release(item));
    }

    setStatus(message, error = false) {
      this.status.textContent = message;
      this.status.classList.toggle('is-error', error);
    }

    validUrl(value, kind) {
      try {
        const url = new URL(value);
        return url.protocol === 'https:' && url.hostname === 'res.cloudinary.com'
          && !url.username && !url.password && !url.port
          && url.pathname.startsWith(`/${this.cloud}/${kind}/upload/`);
      } catch (_) {
        return false;
      }
    }

    restore() {
      try {
        if (this.dataset.submitted === 'true') {
          sessionStorage.removeItem(this.storageKey);
          return;
        }
        const saved = JSON.parse(sessionStorage.getItem(this.storageKey));
        if (!saved || saved.cloud !== this.cloud || Date.now() - saved.time > 2 * 60 * 60 * 1000) return;
        this.items = saved.items.slice(0, MAX_FILES).filter((item) =>
          typeof item.name === 'string' && ['image', 'video'].includes(item.kind)
          && Number.isFinite(item.size) && item.size > 0
        ).map((item) => ({
          name: item.name, size: item.size, kind: item.kind,
          url: this.validUrl(item.url, item.kind) ? item.url : '',
          state: this.validUrl(item.url, item.kind) ? 'done' : 'error',
          error: 'Please remove and select this file again.',
        }));
        if (this.items.length) this.setStatus('Your previous attachments have been restored.');
      } catch (_) {
        // Storage may be unavailable in private browsing. Uploading still works.
      }
    }

    sync() {
      this.links.value = this.items.filter((item) => item.state === 'done').map((item, index) =>
        `${index + 1}. ${item.name.replace(/[\r\n]/g, ' ')} (${formatSize(item.size)})\n${item.url}`
      ).join('\n\n');
      this.links.disabled = !this.links.value;
      try {
        if (!this.items.length) sessionStorage.removeItem(this.storageKey);
        else sessionStorage.setItem(this.storageKey, JSON.stringify({
          cloud: this.cloud, time: Date.now(),
          items: this.items.map(({ name, size, kind, url }) => ({ name, size, kind, url })),
        }));
      } catch (_) { /* The upload and form do not depend on browser storage. */ }
    }

    release(item) {
      item.removed = true;
      item.xhr?.abort();
      if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
    }

    render() {
      this.list.replaceChildren();
      this.items.forEach((item) => {
        const row = document.createElement('li');
        row.className = 'contact-attachments__file';
        if (item.file && !item.previewUrl) item.previewUrl = URL.createObjectURL(item.file);
        if (item.previewUrl) {
          const media = document.createElement(item.kind === 'video' ? 'video' : 'img');
          media.className = 'contact-attachments__preview';
          media.src = item.previewUrl;
          if (item.kind === 'video') {
            media.muted = true;
            media.playsInline = true;
            media.preload = 'metadata';
            media.setAttribute('aria-label', item.name);
          } else media.alt = item.name;
          row.append(media);
        }
        const details = document.createElement('div');
        details.className = 'contact-attachments__details';
        const name = document.createElement('span');
        name.className = 'contact-attachments__name';
        name.textContent = `${item.name} (${formatSize(item.size)})`;
        const state = document.createElement('span');
        state.className = 'contact-attachments__state';
        state.classList.toggle('is-error', item.state === 'error');
        state.textContent = item.state === 'done' ? 'Uploaded'
          : item.state === 'error' ? item.error
          : item.state === 'uploading' ? `Uploading… ${item.progress || 0}%` : 'Waiting to upload…';
        item.stateElement = state;
        details.append(name, state);
        row.append(details);
        const actions = document.createElement('div');
        actions.className = 'contact-attachments__actions';
        const action = (label, handler) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'contact-attachments__action';
          button.textContent = label;
          button.setAttribute('aria-label', `${label} ${item.name}`);
          button.addEventListener('click', handler);
          actions.append(button);
        };
        if (item.state === 'error' && item.file) action('Retry', () => {
          item.state = 'pending';
          this.render();
          this.uploadQueue();
        });
        action('Remove', () => {
          this.items = this.items.filter((entry) => entry !== item);
          this.release(item);
          this.render();
          this.updateStatus();
          this.trigger.focus();
        });
        row.append(actions);
        this.list.append(row);
      });
      this.sync();
    }

    selectFiles() {
      const files = Array.from(this.input.files || []);
      this.input.value = '';
      if (!files.length) return;
      if (this.items.length + files.length > MAX_FILES) {
        this.setStatus('Please select no more than 20 files in total.', true);
        return;
      }
      for (const file of files) {
        const kind = fileKind(file);
        let error = '';
        if (!kind) error = 'Only image and video files are supported.';
        else if (!file.size) error = 'Empty files cannot be uploaded.';
        else if (file.size > (kind === 'image' ? IMAGE_LIMIT : VIDEO_LIMIT)) {
          error = kind === 'image' ? 'Images must be 10MB or smaller.' : 'Videos must be 100MB or smaller.';
        }
        if (error) {
          this.setStatus(`${file.name}: ${error}`, true);
          return;
        }
      }
      this.items.push(...files.map((file) => ({
        file, name: file.name, size: file.size, kind: fileKind(file), state: 'pending', url: '',
      })));
      this.render();
      this.uploadQueue();
    }

    updateStatus() {
      if (this.items.some((item) => item.state === 'error')) {
        this.setStatus('Some files could not be uploaded. Retry or remove them before sending.', true);
      } else if (this.items.some((item) => item.state !== 'done')) {
        this.setStatus('Uploading attachments. Please keep this page open.');
      } else this.setStatus(this.items.length ? `${this.items.length} of ${MAX_FILES} files uploaded.` : '');
    }

    async uploadQueue() {
      if (this.busy) return;
      this.busy = true;
      this.submit.disabled = true;
      this.setAttribute('aria-busy', 'true');
      const signal = this.events.signal;
      try {
        let item;
        while (!signal.aborted && (item = this.items.find((entry) => entry.state === 'pending'))) {
          item.state = 'uploading';
          this.render();
          this.updateStatus();
          try {
            await this.upload(item);
            if (!item.removed) item.state = 'done';
          } catch (error) {
            if (!item.removed) {
              item.state = 'error';
              item.error = error.message || 'Upload failed. Please retry.';
            }
          }
          if (!signal.aborted) this.render();
        }
      } finally {
        this.busy = false;
        this.submit.disabled = false;
        this.removeAttribute('aria-busy');
        if (!signal.aborted) this.updateStatus();
      }
    }

    async upload(item) {
      if (!this.cloud || !this.preset) throw new Error('Attachments are temporarily unavailable. Please try again later.');
      const uploadId = crypto.randomUUID();
      const chunked = item.size > CHUNK_SIZE;
      let result;
      for (let start = 0; start < item.size; start += CHUNK_SIZE) {
        if (item.removed) throw new Error('Upload cancelled.');
        const end = Math.min(start + CHUNK_SIZE, item.size);
        const body = new FormData();
        body.append('file', item.file.slice(start, end), item.name);
        body.append('upload_preset', this.preset);
        body.append('folder', 'fullot-contact-uploads');
        result = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          item.xhr = xhr;
          xhr.open('POST', `https://api.cloudinary.com/v1_1/${encodeURIComponent(this.cloud)}/${item.kind}/upload`);
          xhr.timeout = 120000;
          if (chunked) {
            xhr.setRequestHeader('X-Unique-Upload-Id', uploadId);
            xhr.setRequestHeader('Content-Range', `bytes ${start}-${end - 1}/${item.size}`);
          }
          xhr.upload.onprogress = (event) => {
            if (!event.lengthComputable || item.removed) return;
            item.progress = Math.min(99, Math.floor((start + (end - start) * event.loaded / event.total) / item.size * 100));
            item.stateElement.textContent = `Uploading… ${item.progress}%`;
          };
          xhr.onload = () => {
            let data;
            try { data = JSON.parse(xhr.responseText); } catch (_) {
              reject(new Error('Upload returned an invalid response. Please retry.'));
              return;
            }
            if (xhr.status >= 200 && xhr.status < 300) resolve(data);
            else reject(new Error('Upload failed. Please retry or choose another file.'));
          };
          xhr.onerror = () => reject(new Error('Connection lost. Please retry.'));
          xhr.ontimeout = () => reject(new Error('Upload timed out. Please retry.'));
          xhr.onabort = () => reject(new Error('Upload cancelled.'));
          xhr.send(body);
        });
      }
      item.xhr = null;
      if (result.resource_type !== item.kind || !this.validUrl(result.secure_url, item.kind)) {
        throw new Error('The file could not be verified. Please retry.');
      }
      item.url = result.secure_url;
    }
  }

  customElements.define('contact-attachments', ContactAttachments);
})();
